"use client";

import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Drawer } from "vaul";
import { useHistoryDismiss } from "../../../../hooks/use-history-dismiss";
import { cn, isInAppBrowser } from "../../../../lib/utils";
import {
  RelayShellContext,
  useRelayShellContext,
} from "../../providers/relay-shell-provider";
import type { RelayDrawerDirection, RelayTheme } from "../../types";
import "../../styles/shell.css";

export interface ResponsiveShellProps {
  theme?: RelayTheme;
  mobileBreakpoint?: number;
  drawerDirection?: RelayDrawerDirection;
  dialogWidth?: string;
  drawerMaxWidth?: string;
  drawerMinWidth?: string;
  style?: React.CSSProperties;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

interface KeyboardViewportInset {
  keyboardHeight: number;
  /**
   * 키보드 중 "고정 위치 콘텐츠를 아직 덮고 있는" 높이.
   *
   * `position: fixed` 의 기준은 레이아웃 뷰포트(ICB)다. iOS WKWebView(지갑
   * 인앱 브라우저 포함)는 키보드가 떠도 레이아웃 뷰포트를 안 줄이므로 드로어
   * 하단이 키보드 밑으로 들어간다 — 그만큼 올려줘야 한다. 반대로 안드로이드
   * webview 처럼 뷰포트 자체가 줄어드는 환경은 브라우저가 이미 처리한 것이라
   * 또 올리면 키보드 위에 빈 틈이 생긴다. 줄어든 양을 빼서 두 경우를 한 식으로
   * 덮는다(부분적으로만 줄어드는 환경도 자연히 맞는다).
   */
  keyboardOverlap: number;
  viewportHeight: number;
}

const INPUT_SCROLL_MARGIN = 24;

function isTextInput(target: EventTarget | null): target is HTMLElement {
  return (
    target instanceof HTMLElement &&
    target.matches("input, textarea, select")
  );
}

function getInputScrollDelta(
  targetRect: DOMRect,
  scrollAreaRect: DOMRect,
  viewport?: VisualViewport | null,
): number {
  const viewportTop = viewport?.offsetTop ?? scrollAreaRect.top;
  const viewportBottom = viewport
    ? viewport.offsetTop + viewport.height
    : scrollAreaRect.bottom;
  const safeTop =
    Math.max(scrollAreaRect.top, viewportTop) + INPUT_SCROLL_MARGIN;
  const safeBottom =
    Math.min(scrollAreaRect.bottom, viewportBottom) - INPUT_SCROLL_MARGIN;

  if (targetRect.top < safeTop) return targetRect.top - safeTop;
  if (targetRect.bottom > safeBottom) return targetRect.bottom - safeBottom;
  return 0;
}

/** Tracks the part of the layout viewport occupied by a mobile soft keyboard. */
function useKeyboardViewportInset(): KeyboardViewportInset {
  const layoutViewportHeightRef = React.useRef(0);
  const [inset, setInset] = React.useState<KeyboardViewportInset>({
    keyboardHeight: 0,
    keyboardOverlap: 0,
    viewportHeight: 0,
  });

  React.useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const update = () => {
      const layoutHeight = Math.max(
        window.innerHeight,
        document.documentElement.clientHeight,
      );
      layoutViewportHeightRef.current = Math.max(
        layoutViewportHeightRef.current,
        layoutHeight,
        viewport.height + viewport.offsetTop,
      );
      const keyboardHeight = Math.max(
        0,
        layoutViewportHeightRef.current -
          viewport.height -
          viewport.offsetTop,
      );
      const layoutShrink = Math.max(
        0,
        layoutViewportHeightRef.current - layoutHeight,
      );
      const keyboardOverlap = Math.max(0, keyboardHeight - layoutShrink);
      const viewportHeight = viewport.height;
      setInset((current) =>
        current.keyboardHeight === keyboardHeight &&
        current.keyboardOverlap === keyboardOverlap &&
        current.viewportHeight === viewportHeight
          ? current
          : { keyboardHeight, keyboardOverlap, viewportHeight },
      );
    };

    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, []);

  return inset;
}

/**
 * `useMediaQuery` with a synchronous initial value.
 *
 * The shared hook (`hooks/use-media-query.ts`) starts at `false` and corrects
 * itself in an effect, which is the right call for SSR-hydrated markup but
 * wrong for a shell that can be MOUNTED already-open on a phone: it would paint
 * the dialog branch for a frame and then swap to the drawer. Reading
 * `matchMedia` in the state initializer avoids that frame; the subscription
 * below then tracks the viewport LIVE, so crossing the breakpoint while the
 * modal is open swaps Dialog <-> Drawer immediately (the wizard's state lives
 * above this component -- see RelayWizardStateHost.tsx -- and survives the swap).
 */
function useIsMobileViewport(mobileBreakpoint: number): boolean {
  const query = `(max-width: ${mobileBreakpoint}px)`;
  const [isMobile, setIsMobile] = React.useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(query).matches,
  );

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    setIsMobile(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return isMobile;
}

/**
 * Shared responsive modal shell for the relay deposit widget — radix
 * `Dialog` on desktop, vaul `Drawer` on mobile, switching at
 * `mobileBreakpoint`. Generalized from
 * `WalletConnectModal`/`WalletConnectModalTrigger`/`WalletConnectModalContent`
 * (`packages/dapp-ui/src/features/wallet-connect-modal/components/`): same
 * controlled/uncontrolled open handling, same size-var CSS merge, same
 * Drawer/Dialog branch — but exposing plain compound exports
 * (`ResponsiveShell` / `ShellTrigger` / `ShellContent`) instead of
 * `Object.assign` static properties, since the relay widget owns its own
 * body content rather than a fixed wallet list.
 *
 * The branch follows the viewport LIVE, including while the modal is open:
 * swapping `Dialog.Root` <-> `Drawer.Root` still unmounts everything below it
 * (portal content included), so any consumer with per-open state to preserve
 * must hold that state ABOVE this component — which is exactly what
 * `<RelayDeposit>` does via `RelayWizardStateHost`.
 */
export function ResponsiveShell({
  theme = "dark",
  mobileBreakpoint = 768,
  drawerDirection = "bottom",
  dialogWidth,
  drawerMaxWidth,
  drawerMinWidth,
  style,
  open: openProp,
  onOpenChange,
  children,
}: ResponsiveShellProps) {
  const isMobile = useIsMobileViewport(mobileBreakpoint);
  const [internalOpen, setInternalOpen] = React.useState(false);

  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const handleOpenChange = React.useCallback(
    (value: boolean) => {
      if (!isControlled) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange],
  );

  // Android 백버튼 / iOS 백스와이프로 닫히게 한다.
  useHistoryDismiss(open, React.useCallback(() => handleOpenChange(false), [handleOpenChange]));

  const mergedStyle = React.useMemo(() => {
    const sizeVars: Record<string, string> = {};
    if (dialogWidth) sizeVars["--rd-dialog-width"] = dialogWidth;
    if (drawerMaxWidth) sizeVars["--rd-drawer-max-width"] = drawerMaxWidth;
    if (drawerMinWidth) sizeVars["--rd-drawer-min-width"] = drawerMinWidth;
    return { ...sizeVars, ...style };
  }, [dialogWidth, drawerMaxWidth, drawerMinWidth, style]);

  const contextValue = React.useMemo(
    () => ({
      open,
      setOpen: handleOpenChange,
      isMobile,
      theme,
      drawerDirection,
      portalStyle: mergedStyle,
    }),
    [open, handleOpenChange, isMobile, theme, drawerDirection, mergedStyle],
  );

  const wrapper = (
    <div data-theme={theme} style={mergedStyle}>
      {children}
    </div>
  );

  if (isMobile) {
    return (
      <RelayShellContext.Provider value={contextValue}>
        <Drawer.Root
          key="drawer"
          direction={drawerDirection}
          open={open}
          onOpenChange={handleOpenChange}
          noBodyStyles
        >
          {wrapper}
        </Drawer.Root>
      </RelayShellContext.Provider>
    );
  }

  return (
    <RelayShellContext.Provider value={contextValue}>
      <Dialog.Root open={open} onOpenChange={handleOpenChange}>
        {wrapper}
      </Dialog.Root>
    </RelayShellContext.Provider>
  );
}

export function ShellTrigger({
  asChild,
  children,
  dataAttributes,
}: {
  asChild?: boolean;
  children?: React.ReactNode;
  /**
   * Extra `data-*` attributes to land on the ACTUAL trigger element -- i.e. on
   * the host's own child under `asChild`, since radix/vaul merge a Trigger's
   * props onto the child it renders as.
   *
   * Exists so a compound can expose state a host needs for its own styling
   * without that host having to re-derive it: `<RelayRecovery.Trigger>` passes
   * `data-rd-recovery-count`, letting a custom trigger render its own badge from
   * the same aggregate the default button uses. Deliberately narrow (data
   * attributes only, no arbitrary prop spread) so the shell stays a shell.
   */
  dataAttributes?: Record<`data-${string}`, string | number | undefined>;
}) {
  const { isMobile } = useRelayShellContext();
  const T = isMobile ? Drawer.Trigger : Dialog.Trigger;
  return (
    <T asChild={asChild} {...dataAttributes}>
      {children}
    </T>
  );
}

export function ShellContent({
  className,
  ariaTitle,
  children,
}: {
  className?: string;
  ariaTitle: string;
  children: React.ReactNode;
}) {
  const { open, isMobile, theme, drawerDirection, portalStyle } =
    useRelayShellContext();
  const { keyboardHeight, keyboardOverlap, viewportHeight } =
    useKeyboardViewportInset();
  const isInApp = isInAppBrowser();
  const drawerRef = React.useRef<HTMLDivElement | null>(null);
  const focusTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    },
    [],
  );

  // 열림/닫힘 경계에서만 스크롤을 처음으로 되돌린다. 키보드가 내려갈 때는
  // 건드리지 않는다 — 예전에는 되돌렸는데, 그러면 입력 직후 입력창 아래에
  // 붙는 요약/정보 블록이 화면 밖으로 밀려나 "입력창만 보이는" 상태가 됐다.
  React.useEffect(() => {
    if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    const active = document.activeElement;
    if (!open && isTextInput(active) && drawerRef.current?.contains(active)) {
      active.blur();
    }
    drawerRef.current
      ?.querySelectorAll<HTMLElement>("[data-rd-scroll-area]")
      .forEach((scrollArea) => {
        scrollArea.scrollTo?.({ top: 0, behavior: "auto" });
      });
  }, [open]);

  // 인앱 브라우저(UA)로 걸러내지 않는다 — 지갑 인앱이라도 iOS 는 레이아웃
  // 뷰포트가 안 줄어들어 보정이 필요하고, 줄어드는 환경은 `keyboardOverlap` 이
  // 알아서 0 에 수렴한다. `dvh` 는 키보드를 모르므로 max-height 는 항상 실제
  // 비주얼 뷰포트로 다시 조인다.
  const shouldAdjustForKeyboard =
    isMobile && drawerDirection === "bottom" && keyboardHeight > 0;
  const keyboardStyle: React.CSSProperties | undefined =
    shouldAdjustForKeyboard
      ? {
          bottom: keyboardOverlap,
          maxHeight: `min(85dvh, calc(${viewportHeight}px - env(safe-area-inset-top, 0px)))`,
        }
      : undefined;

  const handleFocusCapture = React.useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (!isMobile || drawerDirection !== "bottom" || !isTextInput(event.target)) {
        return;
      }
      const target = event.target;
      const scrollArea = target.closest<HTMLElement>("[data-rd-scroll-area]");
      if (!scrollArea) return;

      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
      focusTimerRef.current = setTimeout(() => {
        const delta = getInputScrollDelta(
          target.getBoundingClientRect(),
          scrollArea.getBoundingClientRect(),
          window.visualViewport,
        );
        if (Math.abs(delta) >= 1) {
          scrollArea.scrollBy?.({ top: delta, behavior: "auto" });
        }
        focusTimerRef.current = null;
      }, 280);
    },
    [drawerDirection, isMobile],
  );

  if (isMobile) {
    return (
      <Drawer.Portal>
        <Drawer.Overlay className="rd-overlay" />
        <Drawer.Content
          ref={drawerRef}
          className={cn("rd-drawer-content", className)}
          data-theme={theme}
          data-rd-shell="drawer"
          data-rd-in-app={isInApp ? "true" : "false"}
          data-rd-keyboard-adjusted={
            shouldAdjustForKeyboard ? "true" : "false"
          }
          style={{ ...portalStyle, ...keyboardStyle }}
          onFocusCapture={handleFocusCapture}
        >
          <Drawer.Title className="rd-sr-only">{ariaTitle}</Drawer.Title>
          <div className="rd-drawer-handle" />
          {children}
        </Drawer.Content>
      </Drawer.Portal>
    );
  }

  return (
    <Dialog.Portal>
      <Dialog.Overlay className="rd-overlay" />
      <Dialog.Content
        className={cn("rd-dialog-content", className)}
        data-theme={theme}
        data-rd-shell="dialog"
        style={portalStyle}
      >
        <Dialog.Title className="rd-sr-only">{ariaTitle}</Dialog.Title>
        <Dialog.Description className="rd-sr-only">
          {ariaTitle}
        </Dialog.Description>
        {children}
      </Dialog.Content>
    </Dialog.Portal>
  );
}
